import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import UploadModal from '../molecules/UploadModal';

const MobileNavContainer = styled.nav<{ $hideOnMobile?: boolean }>`
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 64px;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 1000; /* Higher z-index to ensure it's above video content */
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: ${({ $hideOnMobile }) => $hideOnMobile ? 'none' : 'flex'};
    justify-content: space-around;
    align-items: center;
  }
`;

interface NavItemProps {
  $active: boolean;
}

const NavItem = styled(Link)<NavItemProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${({ $active }) => $active ? '#fff' : 'rgba(255, 255, 255, 0.6)'};
  text-decoration: none;
  width: 20%;
  height: 100%;
  transition: all 0.2s ease;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    width: ${({ $active }) => $active ? '6px' : '0px'};
    height: 6px;
    background-color: ${({ $active }) => $active ? '#fff' : 'transparent'};
    border-radius: 3px;
    transition: all 0.2s ease;
    opacity: ${({ $active }) => $active ? 1 : 0};
  }
  
  img {
    width: 24px;
    height: 24px;
    margin-bottom: 4px;
    opacity: ${({ $active }) => $active ? 1 : 0.7};
  }
  
  span {
    font-size: 12px;
    font-weight: ${({ $active }) => $active ? 500 : 400};
  }
`;

const UploadButton = styled.button`
  background: none;
  border: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.8);
  width: 20%;
  height: 100%;
  cursor: pointer;
  position: relative;
  
  img {
    width: 28px;
    height: 28px;
    margin-bottom: 4px;
    filter: brightness(1.2);
  }
  
  span {
    font-size: 12px;
    font-weight: 500;
  }
`;

interface MobileNavigationProps {
  onUploadClick: () => void;
  hideOnMobile?: boolean;
}

const MobileNavigation: React.FC<MobileNavigationProps> = ({ onUploadClick, hideOnMobile = false }) => {
  const location = useLocation();
  const pathname = location.pathname;
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  
  const isActive = (path: string) => {
    if (path === '/') {
      return pathname === '/';
    }
    return pathname.startsWith(path);
  };
  
  const handleUploadClick = () => {
    onUploadClick();
  };
  
  return (
    <MobileNavContainer $hideOnMobile={hideOnMobile}>
      <NavItem to="/" $active={isActive('/')}>
        <img 
          src="/nav-icons/home-icon.svg" 
          alt="Home" 
        />
        <span>Home</span>
      </NavItem>
      
      <NavItem to="/content" $active={isActive('/content')}>
        <img 
          src="/nav-icons/content-icon.svg" 
          alt="Content" 
        />
        <span>Content</span>
      </NavItem>
      
      <UploadButton onClick={handleUploadClick}>
        <img 
          src="/nav-icons/add-circle.svg" 
          alt="Upload" 
        />
        <span>Upload</span>
      </UploadButton>
      
      <NavItem to="/wallet" $active={isActive('/wallet')}>
        <img 
          src="/nav-icons/wallet-icon.svg" 
          alt="Wallet" 
        />
        <span>Wallet</span>
      </NavItem>
      
      <NavItem to="/profile" $active={isActive('/profile')}>
        <img 
          src="/nav-icons/profile-icon.svg" 
          alt="Profile" 
        />
        <span>Profile</span>
      </NavItem>
    </MobileNavContainer>
  );
};

export default MobileNavigation;
